// Helper functions for upscaler worker
export const checkOffscreenCanvasSupport = () => {
    return typeof OffscreenCanvas !== 'undefined'
}

export const createUpscalerWorker = () => {
    // Check if worker is supported
    if (typeof Worker === 'undefined') {
        console.warn('Web Workers not supported, falling back to main thread')
        return null
    }

    try {
        return new Worker('/upscaler.worker.js')
    } catch (error) {
        console.warn('Failed to create upscaler worker, falling back to main thread:', error)
        return null
    }
}

export const upscaleWithWorker = (worker, imageSrc, selectedLayer, isPng = false) => {
    return new Promise((resolve, reject) => {
        if (!worker) {
            reject(new Error('Worker not available'))
            return
        }

        // Reduce timeout to 60 seconds for better UX
        const timeout = setTimeout(() => {
            worker.removeEventListener('message', messageHandler)
            worker.removeEventListener('error', errorHandler)
            reject(new Error('Worker timeout - operation took too long'))
        }, 60000) // 1 minute timeout

        const messageHandler = (e) => {
            const {type, data, error} = e.data

            switch (type) {
                case 'PNG_UPSCALE_SUCCESS':
                case 'REGULAR_UPSCALE_SUCCESS':
                    clearTimeout(timeout)
                    worker.removeEventListener('message', messageHandler)
                    worker.removeEventListener('error', errorHandler)
                    // Reconstruct the result with the original selectedLayer
                    resolve({
                        ...data,
                        selectedLayer: selectedLayer,
                    })
                    break

                case 'FALLBACK_TO_MAIN_THREAD':
                    clearTimeout(timeout)
                    worker.removeEventListener('message', messageHandler)
                    worker.removeEventListener('error', errorHandler)
                    reject(new Error('FALLBACK_REQUIRED'))
                    break

                case 'ERROR':
                    clearTimeout(timeout)
                    worker.removeEventListener('message', messageHandler)
                    worker.removeEventListener('error', errorHandler)
                    reject(new Error(error))
                    break

                default:
                    console.warn('Unknown message type from worker:', type)
                    break
            }
        }

        const errorHandler = (error) => {
            clearTimeout(timeout)
            worker.removeEventListener('message', messageHandler)
            worker.removeEventListener('error', errorHandler)
            reject(new Error(`Worker error: ${error.message}`))
        }

        worker.addEventListener('message', messageHandler)
        worker.addEventListener('error', errorHandler)

        // Send upscale request to worker with only serializable data
        const messageType = isPng ? 'UPSCALE_PNG' : 'UPSCALE_REGULAR'
        worker.postMessage({
            type: messageType,
            data: {
                imageSrc,
                layerData: {
                    // Only send serializable properties
                    scaleX: selectedLayer.scaleX,
                    scaleY: selectedLayer.scaleY,
                    offsetX: selectedLayer.offsetX,
                    offsetY: selectedLayer.offsetY,
                    imageType: selectedLayer.imageType,
                },
            },
        })
    })
}

// Fallback function for main thread processing (when worker fails)
export const upscaleOnMainThread = async (upscaler, imageSrc, selectedLayer, isPng = false) => {
    if (isPng) {
        return new Promise((resolve, reject) => {
            const img = new window.Image()
            img.crossOrigin = 'anonymous'

            img.onload = function () {
                // Create canvas to extract alpha channel
                const canvas = document.createElement('canvas')
                const ctx = canvas.getContext('2d')
                canvas.width = img.width
                canvas.height = img.height
                ctx.drawImage(img, 0, 0)

                // Get original image data with alpha
                const originalImageData = ctx.getImageData(0, 0, canvas.width, canvas.height)

                // Upscale the image
                upscaler
                    .upscale(imageSrc, {
                        patchSize: 32, // Reduced patch size for better performance
                        padding: 1,    // Reduced padding for better performance
                    })
                    .then((result) => {
                        // Create new canvas for upscaled image
                        const upscaledImg = new window.Image()
                        upscaledImg.onload = function () {
                            const upscaledCanvas = document.createElement('canvas')
                            const upscaledCtx = upscaledCanvas.getContext('2d')
                            upscaledCanvas.width = upscaledImg.width
                            upscaledCanvas.height = upscaledImg.height

                            // Draw upscaled image
                            upscaledCtx.drawImage(upscaledImg, 0, 0)

                            // Get upscaled image data
                            const upscaledImageData = upscaledCtx.getImageData(
                                0,
                                0,
                                upscaledCanvas.width,
                                upscaledCanvas.height
                            )

                            // Restore alpha channel by scaling up the original alpha values
                            const scaleX = upscaledCanvas.width / canvas.width
                            const scaleY = upscaledCanvas.height / canvas.height

                            // Optimize alpha channel restoration with better performance
                            const origWidth = canvas.width
                            const upscaledWidth = upscaledCanvas.width
                            const upscaledHeight = upscaledCanvas.height

                            for (let y = 0; y < upscaledHeight; y++) {
                                const origY = Math.floor(y / scaleY)
                                const origYIndex = origY * origWidth
                                
                                for (let x = 0; x < upscaledWidth; x++) {
                                    const origX = Math.floor(x / scaleX)
                                    const origIndex = (origYIndex + origX) * 4
                                    const upscaledIndex = (y * upscaledWidth + x) * 4

                                    // Copy alpha value from original image
                                    if (origIndex + 3 < originalImageData.data.length) {
                                        upscaledImageData.data[upscaledIndex + 3] =
                                            originalImageData.data[origIndex + 3]
                                    }
                                }
                            }

                            // Put modified image data back
                            upscaledCtx.putImageData(upscaledImageData, 0, 0)

                            // Convert to blob
                            upscaledCanvas.toBlob((blob) => {
                                // Cleanup canvas elements to free memory
                                canvas.width = 0
                                canvas.height = 0
                                upscaledCanvas.width = 0
                                upscaledCanvas.height = 0
                                
                                resolve({
                                    blob,
                                    width: upscaledImg.width,
                                    height: upscaledImg.height,
                                    selectedLayer,
                                })
                            }, 'image/png')
                        }
                        upscaledImg.src = result
                    })
                    .catch(reject)
            }
            img.src = imageSrc
        })
    } else {
        // Handle non-PNG images
        return upscaler
            .upscale(imageSrc, {
                patchSize: 32, // Reduced patch size for better performance
                padding: 1,    // Reduced padding for better performance
            })
            .then((result) => {
                const base64Data = result.split(',')[1]
                const contentType = result.match(/data:(.*?);base64/)[1] || 'image/png'
                const byteCharacters = atob(base64Data)
                const byteNumbers = new Array(byteCharacters.length)

                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i)
                }

                const byteArray = new Uint8Array(byteNumbers)
                const blob = new Blob([byteArray], {type: contentType})

                return new Promise((resolve) => {
                    const tempImg = new window.Image()
                    tempImg.onload = function () {
                        // Cleanup temp blob URL after getting dimensions
                        URL.revokeObjectURL(tempImg.src)
                        
                        resolve({
                            blob,
                            width: tempImg.width,
                            height: tempImg.height,
                            selectedLayer,
                        })
                    }
                    tempImg.src = URL.createObjectURL(blob)
                })
            })
    }
}

